import { CustomCard } from '@/components';
import { getErrorFromAPI, handleToastMutation } from '@/helpers/toast';
import {
  useGetTrackingLinkQuery,
  useUpdateTrackingLinkMutation,
  useSyncTrackingUrlToShopifyMutation,
} from '@/redux/api/api.caller';
import commonSlice from '@/redux/features/common.slice';
import settingsSlice, { trackingLinkSelector, trackingLinkSyncCooldownEndSelector } from '@/redux/features/settings.slice';
import { ISettingParams } from '@/types/settings';
import { Button, InlineStack, Modal, SkeletonBodyText, Text } from '@shopify/polaris';
import Switch from '@/components/Switch';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { tokenSelector } from '@/redux/features/auth.slice';

const TrackingLink = () => {
  const dispatch = useDispatch();
  const [apiError, setApiError] = useState<string>('');
  const [isOpenOldShipmentsModal, setIsOpenOldShipmentsModal] = useState<boolean>(false);
  const { data, isLoading, status } = useGetTrackingLinkQuery();
  const [updateTrackingLink, updateTrackingLinkStatus] = useUpdateTrackingLinkMutation();
  const [syncTrackingUrlToShopify, { isLoading: isSyncTrackingUrl }] = useSyncTrackingUrlToShopifyMutation();
  const trackingLink = useSelector(trackingLinkSelector);
  const trackingLinkSyncCooldownEnd = useSelector(trackingLinkSyncCooldownEndSelector);
  const authData = useSelector(tokenSelector);

  const isCooldownActive = Date.now() < trackingLinkSyncCooldownEnd;

  useEffect(() => {
    if (status === 'fulfilled') {
      if (data) {
        const convertData = {
          replaceCourierLink: Boolean(data.data?.replaceCourierLink),
          linkDescription: data.data?.linkDescription,
          addLinkToOrder: Boolean(data.data?.addLinkToOrder),
        };
        dispatch(settingsSlice.actions.handleTrackingLink(convertData));
      } else {
        // no initial data
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleToggleReplaceCourierLink = () => {
    const newStatus = !trackingLink.replaceCourierLink;
    const payload: ISettingParams.IUpdateTrackingLinkParams = {
      addLinkToOrder: Number(trackingLink.addLinkToOrder),
      linkDescription: trackingLink.linkDescription,
      replaceCourierLink: Number(newStatus),
    };

    updateTrackingLink(payload).then((res) => {
      if ('data' in res && res.data && res.data?.state === 1) {
        dispatch(commonSlice.actions.handleToast(handleToastMutation(res)));
        const nextTrackingLink = {
          ...trackingLink,
          replaceCourierLink: newStatus,
        };
        dispatch(settingsSlice.actions.handleTrackingLink(nextTrackingLink));
        if (apiError) {
          setApiError('');
        }
        return;
      }
      setApiError(getErrorFromAPI(res));
    });
  };

  const handleOpenOldShipmentsModal = () => {
    setIsOpenOldShipmentsModal(true);
  };

  const handleCloseOldShipmentsModal = () => {
    setIsOpenOldShipmentsModal(false);
  };

  const handleConfirmOldShipments = () => {
    setApiError('');

    syncTrackingUrlToShopify({
      shop: authData.shop,
    }).then((res) => {
      if ('data' in res && res.data && res.data?.state === 1) {
        dispatch(commonSlice.actions.handleToast(handleToastMutation(res)));
        dispatch(settingsSlice.actions.handleTrackingLinkSyncCooldownEnd(Date.now() + 60 * 60 * 1000));
        setIsOpenOldShipmentsModal(false);
        return;
      }
      setApiError(getErrorFromAPI(res));
    });
  };

  return (
    <>
      <CustomCard title="" noHeaderBorder noHeaderPaddingBottom noHeaderPadding>
        {isLoading ? (
          <SkeletonBodyText lines={5} />
        ) : (
          <div>
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h3" variant="headingMd">
                Replace tracking links to your tracking page
              </Text>
              <div style={{ flexShrink: 0 }}>
                <Switch
                  isActive={Boolean(trackingLink.replaceCourierLink)}
                  onClick={handleToggleReplaceCourierLink}
                  tooltip="tracking links"
                  variant="plain"
                  disable={updateTrackingLinkStatus.isLoading}
                />
              </div>
            </InlineStack>

            <div style={{ marginTop: '12px' }}>
              <Text as="p" variant="bodyMd" tone="subdued">
                Redirect customers to your branded tracking page instead of the carrier website. When enabled, tracking links are
                updated across new order details, order history, and order emails.
              </Text>
            </div>
            <div style={{ marginTop: '8px' }}>
              <Button
                variant="plain"
                disabled={!Boolean(trackingLink.replaceCourierLink) || isCooldownActive}
                onClick={handleOpenOldShipmentsModal}
              >
                Replace links for old shipments{isCooldownActive ? ' (Please wait 1 hour before trying again)' : ''}
              </Button>
            </div>
            <Modal
              open={isOpenOldShipmentsModal}
              onClose={handleCloseOldShipmentsModal}
              title="Replace links for old shipments"
              primaryAction={{
                content: 'Confirm',
                onAction: handleConfirmOldShipments,
                loading: isSyncTrackingUrl,
                disabled: isSyncTrackingUrl,
              }}
              secondaryActions={[
                {
                  content: 'Cancel',
                  onAction: handleCloseOldShipmentsModal,
                },
              ]}
            >
              <Modal.Section>
                <Text as="p" variant="bodyMd">
                  Tracking links will be replaced for old shipments in{' '}
                  <Text as="span" fontWeight="bold">
                    last 30 days
                  </Text>{' '}
                  across order details and order history.
                </Text>
              </Modal.Section>
            </Modal>
          </div>
        )}
      </CustomCard>
    </>
  );
};

export default TrackingLink;
